import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/apiConfig';
import './FeedbackForm.css';

const FeedbackForm = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    priority: 'medium',
    type: 'feedback'
  });
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setIsOpen(false);
    }
  };

  // Ensure categories is always an array
  const safeCategories = Array.isArray(categories) ? categories : [];
  
  // Debug logging to identify the issue
  React.useEffect(() => {
    console.log('FeedbackForm Debug - categories:', categories);
    console.log('FeedbackForm Debug - categories type:', typeof categories);
    console.log('FeedbackForm Debug - categories isArray:', Array.isArray(categories));
    console.log('FeedbackForm Debug - safeCategories:', safeCategories);
  }, [categories, safeCategories]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      setCategories([]); // Reset to empty array first
      
      const response = await axios.get(`${API_URL}/api/feedback/categories`);
      
      // Multiple layers of safety checks
      if (response && response.data) {
        const categoriesData = response.data.categories;
        if (Array.isArray(categoriesData) && categoriesData.length >= 0) {
          setCategories(categoriesData);
        } else {
          console.warn('Categories data is not a valid array:', categoriesData);
          setCategories([]);
        }
      } else {
        console.warn('Invalid response structure:', response);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to load categories');
      setCategories([]); // Ensure categories is always an array
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/feedback/submit`, formData, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      setSuccess(true);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        category_id: '',
        priority: 'medium',
        type: 'feedback'
      });

      if (onSubmit) {
        onSubmit(response.data);
      }

      // Auto-close after success
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError(error.response?.data?.error || 'Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  if (success) {
    return (
      <div className="feedback-form-container">
        <div className="feedback-form">
          <button className="close-button" onClick={handleClose} type="button">
            ×
          </button>
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h3>Thank you for your feedback!</h3>
            <p>Your feedback has been submitted successfully. We appreciate your input!</p>
          </div>
          <div className="success-actions">
            <button
              type="button"
              className="submit-button"
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-form-container">
      <div className="feedback-form">
        <div className="feedback-form-header">
          <h2>Share Your Feedback</h2>
          <p>Help us improve JobMockAI by sharing your thoughts and suggestions</p>
          <button className="close-button" onClick={handleClose} type="button">
            ×
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="feedback-form-content">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Brief summary of your feedback"
              required
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label htmlFor="category_id">Category *</label>
            <select
              id="category_id"
              name="category_id"
              value={formData.category_id}
              onChange={handleInputChange}
              required
            >
              <option value="">Select a category</option>
              {(() => {
                try {
                  if (categoriesLoading) {
                    return <option value="" disabled>Loading categories...</option>;
                  }
                  
                  // Triple-check that we have a valid array
                  const validCategories = Array.isArray(safeCategories) ? safeCategories : [];
                  
                  if (validCategories.length === 0) {
                    return <option value="" disabled>No categories available</option>;
                  }
                  
                  // Use traditional for loop instead of map to avoid any potential issues
                  const options = [];
                  for (let i = 0; i < validCategories.length; i++) {
                    const category = validCategories[i];
                    if (category && typeof category === 'object') {
                      options.push(
                        <option key={category.id || `category-${i}`} value={category.id || ''}>
                          {category.name || 'Unknown Category'}
                        </option>
                      );
                    }
                  }
                  
                  return options.length > 0 ? options : <option value="" disabled>No valid categories</option>;
                } catch (error) {
                  console.error('Error rendering categories:', error);
                  return <option value="" disabled>Error loading categories</option>;
                }
              })()}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="type">Type</label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
            >
              <option value="feedback">General Feedback</option>
              <option value="bug_report">Bug Report</option>
              <option value="feature_request">Feature Request</option>
              <option value="improvement">Improvement Suggestion</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="priority">Priority</label>
            <select
              id="priority"
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Please provide detailed feedback, including steps to reproduce if reporting a bug"
              required
              rows={6}
              maxLength={2000}
            />
            <div className="character-count">
              {formData.description.length}/2000 characters
            </div>
          </div>

          <div className="form-actions">
            {onClose && (
              <button type="button" className="cancel-button" onClick={onClose}>
                Cancel
              </button>
            )}
            <button 
              type="submit" 
              className="submit-button" 
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackForm;